// src/components/AppleConnectModal.jsx
//
// Connect an Apple (iCloud) calendar. Unlike Google/Microsoft there is no OAuth
// redirect — Apple uses CalDAV with an app-specific password, so we collect the
// Apple ID + 16-char password and POST them to the API. Self-contained: handles
// the API call, success/error toasts, and the "replaced your active calendar"
// notice, so each dashboard only needs an open/close boolean.

import { useState } from 'react';
import toast from 'react-hot-toast';
import { X, AlertCircle, ExternalLink } from 'lucide-react';
import Modal from './Modal';
import { calendarService } from '../services/calendarService';
import styles from './AppleConnectModal.module.css';

const PROVIDER_LABELS = { google: 'Google Calendar', microsoft: 'Microsoft 365', apple: 'Apple Calendar' };

export default function AppleConnectModal({ isOpen, onClose, onConnected }) {
  const [appleId, setAppleId] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const close = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = appleId.trim();
    const pw = appPassword.trim();
    if (!id || !pw) {
      setError('Enter your Apple ID and app-specific password.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const result = await calendarService.connectApple(id, pw);
      const count = result?.calendars?.length ?? 0;
      const calText = count === 1 ? 'calendar' : 'calendars';

      if (Array.isArray(result?.replaced) && result.replaced.length > 0) {
        const names = result.replaced.map(p => PROVIDER_LABELS[p] || p).join(', ');
        toast.success(`Apple Calendar connected (${count} ${calText}). Replaced ${names}.`);
      } else {
        toast.success(`Apple Calendar connected (${count} ${calText}).`);
      }

      setAppleId('');
      setAppPassword('');
      onConnected?.(result);
      onClose();
    } catch (err) {
      // 401 from the API means bad credentials; surface a clear hint.
      const msg = err?.status === 401
        ? 'Could not sign in to iCloud. Check the Apple ID and that the app-specific password is correct and current.'
        : (err?.message || 'Failed to connect Apple Calendar');
      setError(msg);
      toast.error('Failed to connect Apple Calendar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title="Connect Apple Calendar" size="sm" disableBackdropClose={submitting}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Connect Apple Calendar</h2>
        <button onClick={close} className={styles.closeBtn} aria-label="Close" disabled={submitting}>
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <p className={styles.intro}>
          Apple doesn’t use a sign-in popup. Create an <strong>app-specific password</strong> for
          Vennio, then enter it below with your Apple ID.
        </p>

        <a
          className={styles.helpLink}
          href="https://support.apple.com/en-us/102654"
          target="_blank"
          rel="noopener noreferrer"
        >
          How to create an app-specific password <ExternalLink size={13} aria-hidden="true" />
        </a>

        {error && (
          <div className={styles.errorBox} role="alert">
            <AlertCircle size={20} className={styles.errorIcon} aria-hidden="true" />
            <div>
              <p className={styles.errorTitle}>Couldn’t connect</p>
              <p className={styles.errorMsg}>{error}</p>
            </div>
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="apple-id">Apple ID</label>
          <input
            id="apple-id"
            type="email"
            autoComplete="username"
            value={appleId}
            onChange={(e) => setAppleId(e.target.value)}
            placeholder="you@icloud.com"
            className={styles.input}
            disabled={submitting}
            required
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="apple-app-password">App-specific password</label>
          <input
            id="apple-app-password"
            type="password"
            autoComplete="off"
            value={appPassword}
            onChange={(e) => setAppPassword(e.target.value)}
            placeholder="xxxx-xxxx-xxxx-xxxx"
            className={styles.input}
            disabled={submitting}
            required
          />
          <p className={styles.hint}>16 characters, e.g. <code>abcd-efgh-ijkl-mnop</code>. Not your normal Apple password.</p>
        </div>

        <p className={styles.replaceNote}>
          Connecting Apple replaces your currently active calendar — only one provider syncs at a time.
        </p>

        <div className={styles.footer}>
          <button type="button" onClick={close} disabled={submitting} className={styles.btnCancel}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn-primary">
            {submitting ? 'Connecting…' : 'Connect'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
